import { FC, MouseEvent, ReactElement, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloseIcon from '@mui/icons-material/Close';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { downloadBlob } from '../../../utils/download';
import { QcsHoverExpandButton } from './QcsHoverExpandButton';
import {
  dialogCloseButtonSx,
  dialogContentSx,
  dialogFooterSx,
  dialogHeaderIconBadgeSx,
  dialogHeaderSx,
  dialogHeaderTitleSx,
  exportDialogPaperSx,
  formatToggleGroupSx,
  scopeCardDescSx,
  scopeCardIconSx,
  scopeCardSx,
  scopeCardTitleSx,
  scopeRowSx,
  sectionLabelSx
} from './QcsExportButtonStyles';

export type ExportScope = 'page' | 'all';

export interface ExportOption {
  value: string;
  labelKey: string;
}

interface Props {
  onExport: (format?: string, scope?: ExportScope) => Promise<AxiosResponse<Blob>>;
  fileName?: string;
  options?: ExportOption[];
  scopes?: readonly ExportScope[];
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'error' | 'primary' | 'secondary' | 'success' | 'info' | 'warning';
  sx?: SxProps<Theme>;
}

const BUTTON_SIZE = 40;

export const QcsExportButton: FC<Props> = ({ onExport, fileName, options, scopes, variant = 'text', color, sx }) => {
  const { t } = useTranslation();
  const useDialog = (scopes?.length ?? 0) > 1;
  const singleScope: ExportScope | undefined = scopes?.length === 1 ? scopes[0] : undefined;
  const defaultScope: ExportScope = scopes?.includes('all') ? 'all' : (scopes?.[0] ?? 'all');

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scope, setScope] = useState<ExportScope>(defaultScope);
  const [format, setFormat] = useState<string | undefined>(options?.[0]?.value);

  const doExport = async (fmt?: string, scp?: ExportScope) => {
    setLoading(true);
    try {
      const response = await onExport(fmt, scp);
      await downloadBlob(response, fileName);
    } catch {
      // Error toast is handled by the API layer
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    if (useDialog) {
      setScope(defaultScope);
      setFormat(options?.[0]?.value);
      setDialogOpen(true);
    } else if (options?.length) {
      setAnchorEl(e.currentTarget);
    } else {
      void doExport(undefined, singleScope);
    }
  };

  const handleMenuItemClick = (fmt: string) => {
    setAnchorEl(null);
    void doExport(fmt, singleScope);
  };

  const handleDialogConfirm = () => {
    setDialogOpen(false);
    void doExport(options?.length ? format : undefined, scope);
  };

  return (
    <>
      <QcsHoverExpandButton
        icon={<CloudDownloadIcon />}
        onClick={handleClick}
        loading={loading}
        disabled={loading}
        variant={variant}
        color={color}
        size={BUTTON_SIZE}
        ariaLabel={t('common.export')}
        sx={sx}
        expandDirection={'left'}
      >
        {t('common.export')}
      </QcsHoverExpandButton>

      {!useDialog && options?.length && (
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
          {options.map((opt) => (
            <MenuItem key={opt.value} onClick={() => handleMenuItemClick(opt.value)}>
              {t(opt.labelKey)}
            </MenuItem>
          ))}
        </Menu>
      )}

      {useDialog && scopes && (
        <ExportDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleDialogConfirm}
          scopes={scopes}
          scope={scope}
          onScopeChange={setScope}
          options={options}
          format={format}
          onFormatChange={setFormat}
        />
      )}
    </>
  );
};

interface DialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  scopes: readonly ExportScope[];
  scope: ExportScope;
  onScopeChange: (s: ExportScope) => void;
  options?: ExportOption[];
  format?: string;
  onFormatChange: (f: string) => void;
}

const SCOPE_META: Record<ExportScope, { icon: ReactElement; titleKey: string; descKey: string }> = {
  page: {
    icon: <ArticleOutlinedIcon />,
    titleKey: 'common.exportDialog.scopePage',
    descKey: 'common.exportDialog.scopePageDesc',
  },
  all: {
    icon: <LibraryBooksOutlinedIcon />,
    titleKey: 'common.exportDialog.scopeAll',
    descKey: 'common.exportDialog.scopeAllDesc',
  },
};

const ExportDialog: FC<DialogProps> = ({ open, onClose, onConfirm, scopes, scope, onScopeChange, options, format, onFormatChange }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: exportDialogPaperSx } }}>
      <Box sx={dialogHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={dialogHeaderIconBadgeSx}>
            <CloudDownloadIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" sx={dialogHeaderTitleSx}>
              {t('common.exportDialog.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('common.exportDialog.subtitle')}
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label={t('common.close')} sx={dialogCloseButtonSx}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={dialogContentSx}>
        <Typography variant="overline" sx={sectionLabelSx}>
          {t('common.exportDialog.scopeTitle')}
        </Typography>
        <Stack direction="row" spacing={1.25} sx={scopeRowSx(!!options?.length)}>
          {scopes.map((s) => {
            const meta = SCOPE_META[s];
            const active = scope === s;
            return (
              <Box
                key={s}
                role="button"
                tabIndex={0}
                aria-pressed={active}
                onClick={() => onScopeChange(s)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onScopeChange(s);
                  }
                }}
                sx={scopeCardSx(active)}
              >
                <Box sx={scopeCardIconSx(active)}>{meta.icon}</Box>
                <Typography variant="subtitle2" sx={scopeCardTitleSx}>
                  {t(meta.titleKey)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={scopeCardDescSx}>
                  {t(meta.descKey)}
                </Typography>
              </Box>
            );
          })}
        </Stack>

        {options?.length ? (
          <>
            <Typography variant="overline" sx={sectionLabelSx}>
              {t('common.exportDialog.formatTitle')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={format ?? options[0].value}
              onChange={(_, v) => v && onFormatChange(v)}
              size="small"
              sx={formatToggleGroupSx}
            >
              {options.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </>
        ) : null}
      </DialogContent>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={dialogFooterSx}>
        <Button onClick={onClose} variant="text" color="inherit">
          {t('common.cancel')}
        </Button>
        <Button onClick={onConfirm} variant="contained" startIcon={<CloudDownloadIcon />}>
          {t('common.exportDialog.confirm')}
        </Button>
      </Stack>
    </Dialog>
  );
};
